import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Bot } from 'lucide-react';
import { BRAND } from '../data/brand';
import { useTranslation, translateVi } from '../i18n/LocaleContext';

const SUPPORT_EMAIL = `support@${BRAND.name.toLowerCase().replace(/[^a-z0-9]/g, '')}.com`;

const ChatBot = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState(() => ([
    {
      id: 1,
      text: t('botGreeting'),
      sender: 'bot',
      timestamp: new Date()
    }
  ]));
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (!inputText.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      text: inputText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages([...messages, userMessage]);
    setInputText('');

    // Auto reply after 1 second
    setTimeout(() => {
      const botReply = {
        id: messages.length + 2,
        text: getBotReply(inputText),
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botReply]);
    }, 1000);
  };

  const getBotReply = (userInput) => {
    const input = userInput.toLowerCase();

    // 关键词匹配保留越南语原文,并兼容英文提问;回复按当前界面语言输出
    if (input.includes('nạp tiền') || input.includes('nap tien') || input.includes('deposit') || input.includes('top up')) {
      return translateVi('Để nạp tiền, bạn vào mục Ví → Nạp tiền → Nhập số tiền → Quét mã QR để chuyển khoản nhé!');
    } else if (input.includes('rút tiền') || input.includes('rut tien') || input.includes('withdraw')) {
      return translateVi('Rút tiền rất đơn giản: Vào Ví → Rút tiền → Chọn tài khoản → Nhập số tiền và mã PIN. Thời gian xử lý từ 1-24 giờ làm việc.');
    } else if (input.includes('hoa hồng') || input.includes('hoa hong') || input.includes('commission')) {
      return translateVi('Hoa hồng phụ thuộc vào cấp VIP của bạn, từ 0.3% đến 2%. Cấp VIP càng cao, hoa hồng càng nhiều!');
    } else if (input.includes('vip')) {
      return translateVi('Để nâng cấp VIP, bạn cần hoàn thành đủ số lượng đơn hàng yêu cầu. Mỗi cấp VIP có quyền lợi khác nhau về hoa hồng và số đơn hàng.');
    } else if (input.includes('hotline') || input.includes('liên hệ') || input.includes('contact')) {
      return translateVi('Bạn có thể liên hệ qua:\nHotline: 1900-xxxx (8:00 - 22:00)\nEmail: ') + SUPPORT_EMAIL;
    } else {
      return translateVi('Cảm ơn bạn đã liên hệ! Nếu cần hỗ trợ thêm, vui lòng gọi hotline 1900-xxxx hoặc email ') + SUPPORT_EMAIL;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl w-full max-w-md h-[600px] max-h-full flex flex-col overflow-hidden shadow-lift">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-black/5 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center">
              <Bot className="w-5 h-5" />
            </span>
            <div>
              <h3 className="text-[15px] font-bold text-ink leading-tight">{t('botTitle')}</h3>
              <p className="text-[11.5px] text-ink-muted leading-tight mt-0.5">{t('alwaysReady')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label={t('close')}
            className="w-9 h-9 rounded-full bg-canvas flex items-center justify-center text-ink-secondary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 bg-canvas">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`mb-4 flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                  message.sender === 'user'
                    ? 'bg-brand-500 text-white rounded-br-md'
                    : 'bg-white text-ink shadow-card rounded-bl-md'
                }`}
              >
                <p className="text-sm whitespace-pre-line">{message.text}</p>
                <p className={`text-[10.5px] mt-1 ${
                  message.sender === 'user' ? 'text-white/70' : 'text-ink-muted'
                }`}>
                  {message.timestamp.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="flex-shrink-0 p-4 bg-white border-t border-black/5">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder={t('typeMessage')}
              className="flex-1 min-w-0 px-4 h-11 bg-canvas rounded-xl text-sm text-ink placeholder:text-ink-muted border-0 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button
              onClick={handleSend}
              disabled={!inputText.trim()}
              className="w-11 h-11 rounded-lg bg-brand-600 text-white flex items-center justify-center transition-all active:bg-brand-700 disabled:opacity-40 flex-shrink-0"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatBot;
